import { Link, useNavigate } from "@tanstack/react-router";
import {
  flexRender,
  useTable,
  type ColumnDef,
  type ColumnFiltersState,
  type Row,
  type SortingState,
  type PaginationState,
  type OnChangeFn,
  type RowData,
  type Updater,
} from "@tanstack/react-table";
import { Icon, type IconName } from "icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Table as UiTable,
  TableBody,
  TableCell as UiTableCell,
  TableHead,
  TableHeader,
  TableRow as UiTableRow,
  cn,
} from "ui";

import { DataTableContext } from "src/components/DataTableContext";
import { DataTableEmpty } from "src/components/DataTableEmpty";
import {
  dataTableFeatures,
  type DataTableFeatures,
} from "src/components/dataTableFeatures";
import { DataTableLoading } from "src/components/DataTableLoading";
import { DataTablePagination } from "src/components/DataTablePagination";
import { DataTableRowActions } from "src/components/DataTableRowActions";
import { DataTableToolbar } from "src/components/DataTableToolbar";
import {
  type FilterAction,
  type FilterGroupConfig,
  getActiveFilterCount,
  type SelectedFilters,
} from "src/components/filterConfig";

export { DataTableSortHeader } from "src/components/DataTableSortHeader";

const iconClassName = "size-4";

export interface DialogConfig {
  title?: string;
  description?: string;
  destructive?: boolean;
  body?: React.ReactNode;
  content?: React.ReactNode;
}

export interface RowAction<TData = unknown, TError = unknown> {
  id?: string;
  label?: string;
  destructive?: boolean;
  icon?: React.ReactNode | React.ComponentType<{ className?: string }>;
  onSelect?: () => void;
  content?: React.ReactNode;
  isLoading?: boolean;
  dialog?: DialogConfig;
  mutationFn?: () => Promise<TData>;
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
  onSettled?: (data: TData | undefined, error: TError | null) => void;
}

export type RowActionInput =
  | RowAction<unknown, unknown>
  | null
  | undefined
  | false;

export const prebuiltActions = {
  edit: {
    label: "Edit",
    icon: <Icon name="tabler-edit" />,
  },
  copy: {
    label: "Copy",
    icon: <Icon name="tabler-copy" />,
  },
  view: {
    label: "View",
    icon: <Icon name="tabler-eye" />,
  },
  delete: {
    label: "Delete",
    destructive: true as const,
    icon: <Icon name="tabler-trash" />,
  },
} as const;

export function createAction<TData = unknown, TError = unknown>(
  action: RowAction<TData, TError>,
): RowAction {
  return action as RowAction;
}

export interface DataTableEmptyState {
  icon: IconName;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
}

export interface DataTableFiltersConfig {
  config: FilterGroupConfig[];
  selectedFilters: SelectedFilters;
  onAction: (action: FilterAction) => void;
  saveAction?: {
    label: string;
    onSelect: () => void;
  };
}

export interface DataTableExternalFilterState {
  hasActiveFilters: boolean;
  hideClearAction?: boolean;
  clearFilters: () => void;
}

export interface DataTableProps<TData extends RowData> {
  columns: ColumnDef<DataTableFeatures, TData, unknown>[];
  data: TData[];
  getRowId?: (row: TData, index: number) => string;
  rowHref?: (row: Row<DataTableFeatures, TData>) => string | undefined;
  onRowClick?: (row: Row<DataTableFeatures, TData>) => void;
  selectOnRowClick?: boolean;
  isRowSelected?: (row: Row<DataTableFeatures, TData>) => boolean;
  searchColumnId?: string;
  searchPlaceholder?: string;
  filters?: DataTableFiltersConfig;
  externalFilterState?: DataTableExternalFilterState;
  rowActions?: (row: Row<DataTableFeatures, TData>) => RowActionInput[];
  renderExpandedRow?: (row: Row<DataTableFeatures, TData>) => React.ReactNode;
  expandMode?: "single" | "multiple";
  pageSize?: number;
  pageSizeOptions?: number[];
  toolbarTrailing?: React.ReactNode;
  toolbarSecondaryContent?: React.ReactNode;
  toolbarContent?: React.ReactNode;
  isFetching?: boolean;
  isLoading?: boolean;
  hideToolbar?: boolean;
  hidePagination?: boolean;
  hideRowsPerPage?: boolean;
  emptyState?: DataTableEmptyState;
  className?: string;

  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;

  manualPagination?: boolean;
  manualFiltering?: boolean;
  manualSorting?: boolean;
  rowCount?: number;
  pageCount?: number;
}

export function DataTable<TData extends RowData>(props: DataTableProps<TData>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [internalColumnFilters, setInternalColumnFilters] =
    useState<ColumnFiltersState>([]);
  const [internalGlobalFilter, setInternalGlobalFilter] = useState("");
  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    {
      pageIndex: 0,
      pageSize:
        props.pageSize ?? (props.hidePagination ? Number.MAX_SAFE_INTEGER : 10),
    },
  );

  const sorting = props.sorting ?? internalSorting;
  const columnFilters = props.columnFilters ?? internalColumnFilters;
  const globalFilter = props.globalFilter ?? internalGlobalFilter;
  const pagination = props.pagination ?? internalPagination;
  const externalSearch = props.searchColumnId
    ? ((columnFilters.find((filter) => {
        return filter.id === props.searchColumnId;
      })?.value as string | undefined) ?? "")
    : globalFilter;
  const [search, setSearch] = useState(externalSearch);

  useEffect(() => {
    setSearch(externalSearch);
  }, [externalSearch]);

  function handleSortingChange(updater: Updater<SortingState>) {
    const next = typeof updater === "function" ? updater(sorting) : updater;

    if (props.onSortingChange) {
      props.onSortingChange(next);
    } else {
      setInternalSorting(next);
    }

    resetToFirstPage();
  }

  function handleColumnFiltersChange(updater: Updater<ColumnFiltersState>) {
    const next =
      typeof updater === "function" ? updater(columnFilters) : updater;

    if (props.onColumnFiltersChange) {
      props.onColumnFiltersChange(next);
    } else {
      setInternalColumnFilters(next);
    }

    resetToFirstPage();
  }

  function handleGlobalFilterChange(value: string) {
    if (props.onGlobalFilterChange) {
      props.onGlobalFilterChange(value);
    } else {
      setInternalGlobalFilter(value);
    }

    resetToFirstPage();
  }

  function handlePaginationChange(updater: Updater<PaginationState>) {
    const next = typeof updater === "function" ? updater(pagination) : updater;

    if (props.onPaginationChange) {
      props.onPaginationChange(next);
    } else {
      setInternalPagination(next);
    }
  }

  function resetToFirstPage() {
    if (pagination.pageIndex === 0) return;

    const next = { ...pagination, pageIndex: 0 };

    if (props.onPaginationChange) {
      props.onPaginationChange(next);
    } else {
      setInternalPagination(next);
    }
  }

  const hasRowActions = !!props.rowActions;
  const hasExpansion = !!props.renderExpandedRow;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  function toggleExpanded(rowId: string) {
    setExpanded((prev) => {
      const isOpen = !!prev[rowId];

      if (props.expandMode === "single") {
        return isOpen ? {} : { [rowId]: true };
      }

      return { ...prev, [rowId]: !isOpen };
    });
  }

  const columns = React.useMemo(() => {
    const result: ColumnDef<DataTableFeatures, TData, unknown>[] = [];

    if (hasExpansion) {
      result.push({
        id: "__expander",
        header: () => {
          return null;
        },
        enableSorting: false,
        enableHiding: false,
        size: 40,
        cell: () => {
          return null;
        },
      });
    }

    result.push(...props.columns);

    if (hasRowActions) {
      result.push({
        id: "__actions",
        header: () => {
          return <span className="sr-only">{t("dataTable.actions")}</span>;
        },
        enableSorting: false,
        enableHiding: false,
        size: 52,
        cell: ({ row }) => {
          return (
            <DataTableRowActions row={row} rowActions={props.rowActions!} />
          );
        },
      });
    }

    return result;
  }, [props.columns, hasRowActions, hasExpansion, props.rowActions, t]);

  const table = useTable({
    features: dataTableFeatures,
    data: props.data,
    columns,
    defaultColumn: { size: undefined },
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination,
    },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onGlobalFilterChange: handleGlobalFilterChange,
    onPaginationChange: handlePaginationChange,
    getRowId: props.getRowId,
    manualSorting: props.manualSorting,
    manualFiltering: props.manualFiltering,
    manualPagination: props.manualPagination,
    rowCount: props.rowCount,
    pageCount: props.pageCount,
  });

  const rows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleLeafColumns();

  function renderColGroup() {
    return (
      <colgroup>
        {visibleColumns.map((column) => {
          const size = column.columnDef.size;
          return (
            <col
              key={column.id}
              style={size ? { width: `${size}px` } : undefined}
            />
          );
        })}
      </colgroup>
    );
  }

  const configurableFilterCount = props.filters
    ? getActiveFilterCount(props.filters.config, props.filters.selectedFilters)
    : 0;
  const hasActiveFilters =
    search.length > 0 ||
    columnFilters.length > 0 ||
    configurableFilterCount > 0 ||
    props.externalFilterState?.hasActiveFilters === true;

  function handleToolbarSearchChange(value: string) {
    setSearch(value);

    if (props.searchColumnId) {
      table
        .getColumn(props.searchColumnId)
        ?.setFilterValue(value === "" ? undefined : value);
      return;
    }

    handleGlobalFilterChange(value);
  }

  function handleFilterAction(action: FilterAction) {
    props.filters?.onAction(action);
  }

  function handleResetAll() {
    setSearch("");
    handleColumnFiltersChange([]);
    handleGlobalFilterChange("");
    props.filters?.onAction({ type: "filters-cleared" });
    props.externalFilterState?.clearFilters();
  }

  const contextValue = {
    search,
    searchPlaceholder: props.searchPlaceholder,
    isFetching: props.isFetching ?? false,
    filterConfig: props.filters?.config ?? [],
    selectedFilters: props.filters?.selectedFilters ?? {},
    hasActiveFilters,
    hideClearFilters: props.externalFilterState?.hideClearAction === true,
    toolbarTrailing: props.toolbarTrailing,
    toolbarSecondaryContent: props.toolbarSecondaryContent,
    onSearchChange: handleToolbarSearchChange,
    onFilterAction: handleFilterAction,
    clearFilters: handleResetAll,
    filterSaveAction: props.filters?.saveAction,
  };

  return (
    <div
      className={cn(
        "min-h-0 overflow-hidden rounded-lg border bg-card text-card-foreground flex flex-col",
        props.className,
      )}
    >
      {!props.hideToolbar ? (
        <DataTableContext.Provider value={contextValue}>
          {props.toolbarContent ? (
            <div className="border-b p-3">{props.toolbarContent}</div>
          ) : (
            <DataTableToolbar />
          )}
        </DataTableContext.Provider>
      ) : null}

      <div className="bg-card overflow-auto [scrollbar-gutter:stable]">
        <UiTable className="table-fixed">
          {renderColGroup()}
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => {
              return (
                <UiTableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id} className="h-10">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </UiTableRow>
              );
            })}
          </TableHeader>
        </UiTable>
      </div>

      <div className="min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable]">
        <UiTable className="table-fixed">
          {renderColGroup()}
          <TableBody>
            {rows.length === 0 ? (
              <UiTableRow className="hover:bg-transparent">
                <UiTableCell colSpan={columns.length} className="h-48">
                  <DataTableContext.Provider value={contextValue}>
                    {props.isLoading ? (
                      <DataTableLoading />
                    ) : hasActiveFilters ? (
                      <DataTableEmpty />
                    ) : (
                      <DataTableEmpty state={props.emptyState} />
                    )}
                  </DataTableContext.Provider>
                </UiTableCell>
              </UiTableRow>
            ) : (
              rows.map((row) => {
                const href = props.rowHref?.(row);
                const clickable = !!href || !!props.onRowClick;
                const isExpanded = !!expanded[row.id];
                const isSelected = props.isRowSelected?.(row) ?? false;
                return (
                  <React.Fragment key={row.id}>
                    <UiTableRow
                      data-state={isSelected ? "selected" : undefined}
                      className={cn(
                        "relative [transform:translate(0)] [clip-path:inset(0)] focus-within:outline-2 focus-within:outline-ring focus-within:-outline-offset-2",
                        {
                          "cursor-pointer": clickable,
                          "bg-primary/5 hover:bg-primary/10": isSelected,
                        },
                      )}
                      onClick={
                        clickable
                          ? (e) => {
                              const target = e.target as HTMLElement;

                              if (!e.currentTarget.contains(target)) return;

                              const interactiveTarget = props.selectOnRowClick
                                ? target.closest(
                                    "a, input, textarea, select, [role='checkbox'], [role='switch'], [role='dialog']",
                                  )
                                : target.closest(
                                    "a, button, input, textarea, select, [role='button'], [role='menuitem'], [role='dialog']",
                                  );

                              if (
                                interactiveTarget &&
                                e.currentTarget.contains(interactiveTarget)
                              ) {
                                return;
                              }

                              if (href) {
                                navigate({ to: href });
                                return;
                              }

                              props.onRowClick?.(row);
                            }
                          : undefined
                      }
                    >
                      {row.getVisibleCells().map((cell, index) => {
                        const isFirst = index === 0;
                        const isExpander = cell.column.id === "__expander";

                        if (isExpander) {
                          return (
                            <UiTableCell
                              key={cell.id}
                              className="px-4 py-3 align-middle"
                              style={{ width: 40 }}
                            >
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-expanded={isExpanded}
                                aria-label={t("dataTable.openRow")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  toggleExpanded(row.id);
                                }}
                              >
                                {isExpanded ? (
                                  <Icon
                                    name="tabler-chevron-up"
                                    className={iconClassName}
                                  />
                                ) : (
                                  <Icon
                                    name="tabler-chevron-down"
                                    className={iconClassName}
                                  />
                                )}
                              </Button>
                            </UiTableCell>
                          );
                        }

                        return (
                          <UiTableCell
                            key={cell.id}
                            className="px-4 py-3 align-middle"
                          >
                            {href && isFirst && (
                              <Link
                                to={href}
                                className="absolute inset-0"
                                aria-label={t("dataTable.openRow")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              />
                            )}
                            <div className="pointer-events-none relative [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_[data-slot='tooltip-trigger']]:pointer-events-auto [&_[role='menuitem']]:pointer-events-auto">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </div>
                          </UiTableCell>
                        );
                      })}
                    </UiTableRow>
                    {hasExpansion && isExpanded && (
                      <UiTableRow className="hover:bg-transparent">
                        <UiTableCell
                          colSpan={columns.length}
                          className="bg-muted/30 p-0"
                        >
                          {props.renderExpandedRow!(row)}
                        </UiTableCell>
                      </UiTableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </UiTable>
      </div>

      {!props.hidePagination && (
        <DataTablePagination
          table={table}
          pageSizeOptions={props.pageSizeOptions ?? [10, 20, 30, 50]}
          rowCount={props.rowCount}
          hideRowsPerPage={props.hideRowsPerPage}
        />
      )}
    </div>
  );
}
